import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import Api from '../utils/Api';
import { useError } from './ErrorContext';
import { useLoading } from './LoadingContext';
import {useNavigate, useLocation,useSearchParams } from 'react-router-dom';
import { useRoutePath } from '../utils/getRoutePath';

const RoleContext = createContext();

export const useRole = () => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};

export const RoleProvider = ({ children }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { hideLoading } = useLoading();
  const { getToken } = useAuth();
  const [api, setApi] = useState(null);
  const { handleApiError, formErrors, clearErrors } = useError();
  const [roles, setRoles] = useState([]);
  const segments = location.pathname.split('/');
  const id = segments.pop();
  const [paginate,setPaginate] = useState(true)
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/roles')
   
  const getRoles = useCallback(async (page = 1,filters = {}) => {
    // Prevent multiple simultaneous requests
   
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
     
      return;
    }
    
    try {
       
        let query = '';
        if(filters?.q){
          query = '&q='+filters?.q;
          
        } 
        if(filters?.active){
          query += '&active='+filters?.active;
        } 
        const searchParams = new URLSearchParams(window.location.search);
       
        const q = searchParams.get('q') || '';
        const active = searchParams.get('active') || '';
        if(q ){
          query += '&q='+q
        }
        if(active){
          query += '&active='+active;
        }   
       
        let response = null;
        if(filters?.all){
           let query = '?all=true';
          
          if(filters?.is_admin){
            query += '&is_admin='+filters?.is_admin;
          }
         
          response = await apiInstance.call('roles'+query , 'GET',null,true,false);
          setPaginate(false);
        }else{
          response = await apiInstance.call('roles?page=' + page+'&paginate='+paginate +''+query , 'GET');
        }
      
      
      if (response.status === 200) {
        
        setRoles(response.data.roles || []);
        setPaginate(response.data.paginate);
        hideLoading();
        if(filters){
          return response.data.roles;
        }
       
      } else {
        hideLoading();
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch roles'
        });
      }
     
    } catch (error) {
     
      hideLoading();
    } finally {
      
    }
  }, [handleApiError, hideLoading, getToken,paginate]);

   
  // Initialize API and roles only once
  useEffect(() => {
    const initializeApi = async () => {
       
      try {
        const apiInstance = Api(() => getToken());
        let page = parseInt(searchParams.get('page')) || 1;
        setApi(apiInstance,page);
        
        // Load initial roles
        await getRoles(page);
      } catch (error) {
       
      }
    };
    if(initialized){
     
      initializeApi();
    }
  }, [getToken, getRoles, initialized,searchParams]);

  const getRoleByIdApi = useCallback(async (apiInstance,id) => {
    
    if (!apiInstance) {
     
      return;
    }
    const response = await apiInstance.call(`roles/${id}`, 'GET', null, true, true);
   
    if (response.status === 200) {
   
      let roles = {data:[response?.data?.role]};
      
      setRoles(roles || []);
      
    }
  },[]);
  

  useEffect(() => {
    const handleLoad = async() => {
      const currentPath = window.location.pathname;
       
      if(currentPath.includes(getRoutePath('/roles/edit/'+id))){
          
        if (getToken()) {
          const apiInstance = Api(() => getToken());
      
          setApi(apiInstance);
          await getRoleByIdApi(apiInstance,id);
         
        }
      }
     
      
    };

    // Add onload listener
    window.addEventListener('load', handleLoad);

    // Cleanup listener
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [getRoutePath,id,getToken,getRoleByIdApi]);


  const addRole = async (roleData) => {
    
    if (!api) {
       return;
    }

    try {
      const response = await api.call('roles', 'POST', roleData);
      if (response.status === 200) {
        const roles = response.data.roles;
        setRoles(roles);
        
        return { status: response.status };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          return handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to add role'
          });
        }
      }
    } catch (error) {
      
      return handleApiError(error, {
        showAlert: false,
        redirectOn401: true,
        customMessage: 'Failed to add role'
      });
    }
  };

  const updateRole = async (id, roleData) => {
     
    if (!api) {
       
      return;
    }

    try {
      const response = await api.call(`roles/${id}`, 'PUT', roleData);
      if (response.status === 200) {
        const roles = response.data.roles;
        setRoles(roles);
        return { status: response.status };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
        
          return handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to update role'
          });
        }
      }
    } catch (error) {
      
      return handleApiError(error, {
        showAlert: false,
        redirectOn401: true,
        customMessage: 'Failed to update role'
      });
    }
  };

  const deleteRole = async (id) => {
    if (!api) {
     
      return;
    }

    try {
      const response = await api.call(`roles/${id}`, 'DELETE');
      if (response.status === 200) {
        const roles = response.data.roles;
        setRoles(roles);
        navigate(getRoutePath('/roles'));
        return { status: response.status };
      } else {
        return handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to delete role'
        });
      }
    } catch (error) {
      
      return handleApiError(error, {
        showAlert: false,
        redirectOn401: true,
        customMessage: 'Failed to delete role'
      });
    }
  };

  const getRoleById = (id) => {
 
    let roleData = roles?.data?.find(role => {
      return Number(role.id) === Number(id);
    });
    return roleData;
  };

  return (
    <RoleContext.Provider
      value={{
        roles,
        getRoles: (page,filters) => getRoles(page,filters),
        addRole,
        updateRole,
        deleteRole,
        getRoleById,
        formErrors,
        paginate,
        setPaginate,
        clearErrors,
        
      }}
    >
      {children}
    </RoleContext.Provider>
  );
}; 